import { useQuery } from '@tanstack/react-query';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getSharedApiBaseUrl } from '@/lib/config';

interface PersonNameRaw {
  person_id: string;
  first_name: string;
  last_name: string;
}

async function fetchPersonNames(personIds: string[]): Promise<Map<string, string>> {
  if (personIds.length === 0) return new Map();

  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('No auth session');

  // PLM-1112: POST /persons (R1 in DesignOutput `Design Decisions/
  // person-and-patient-read-routes.md`) replaces /persons/get. It answers
  // { entries: [...] } for one id or many; an id the caller may not see is
  // absent, so that row keeps showing the raw person id.
  const res = await fetch(`${getSharedApiBaseUrl()}/shared/public/identity/persons`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ entries: personIds.map((personId) => ({ personId })) }),
  });

  if (!res.ok) throw new Error(`Failed to fetch person names: HTTP ${res.status}`);

  const json = (await res.json()) as { entries: PersonNameRaw[] };
  const map = new Map<string, string>();
  for (const p of json.entries) {
    map.set(String(p.person_id), `${p.first_name} ${p.last_name}`.trim());
  }
  return map;
}

export function usePersonNames(personIds: string[]) {
  const uniqueIds = [...new Set(personIds.filter(Boolean))].sort();
  const key = uniqueIds.join(',');

  return useQuery({
    queryKey: ['persons', 'names', key],
    queryFn: () => fetchPersonNames(uniqueIds),
    enabled: uniqueIds.length > 0,
    staleTime: 10 * 60_000,
  });
}
